import React, { useEffect, useState } from "react";
import { Typography, Button, Divider } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { brand } from "../../../theme/themePrimitives";
import { usePortal } from "../../../context/PortalsContext";
import {
  getEmailTemplates,
  deleteEmailTemplate,
} from "../../../services/api/apiClient";
import {
  EmailTemplateResult,
  EmailTemplatesPaginatedResult,
} from "../../../services/api/types/emailTemplates";
import { useNavigate } from "react-router-dom";
import { REMOVETEXT } from "../../../utils/constants";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { useAuth } from "../../../context/AuthContext";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { CustomActionIconLink } from "../../../components/CustomActionIconLink/CustomActionIconLink";
import {
  DataTable,
  ColumnDef,
  SortOrder,
} from "../../../components/DataTable/DataTable";
import { useToast } from "../../../context/ToastContext";

type SortField = "name" | "subject" | "sender" | "type";

const EmailTemplates: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState(1);
  const [templates, setTemplates] = useState<EmailTemplateResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortOrder>("asc");
  const [selectedTemplates, setSelectedTemplates] = useState<
    EmailTemplateResult[]
  >([]);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { portals } = usePortal();
  const { auth } = useAuth();
  const portalId = portals.selectedPortal?.portalUid || "";
  const { showToast } = useToast();
  const anySelected = selectedTemplates.length > 0;

  usePageMetadata(
    {
      title: "Email templates",
      breadcrumbs: [
        {
          label: "Email management",
          path: `/portal-configuration/${portalId}/email-send`,
        },
        {
          label: "Email Templates",
        },
      ],
    },
    [portalId],
  );

  // Load data from API with search, sort, and pagination parameters
  // NOTE: Search is done server-side via API, not client-side filtering
  useEffect(() => {
    const load = async () => {
      if (!portalId) return;
      try {
        setIsLoading(true);
        const data = (await getEmailTemplates(portalId, {
          name: searchQuery || undefined, // Search query sent to API
          short: false,
          orderBy: sortField,
          sort: sortDirection,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
        })) as EmailTemplatesPaginatedResult;
        setTemplates(Array.isArray(data.rows) ? data.rows : []);
        setTotalCount(data.count || 0);
      } catch (err) {
        console.error("Failed to load email templates", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [portalId, searchQuery, sortField, sortDirection, rowsPerPage, page]);

  const handleSortChange = (orderBy: string, order: SortOrder) => {
    setSortField(orderBy as SortField);
    setSortDirection(order);
    setPage(1);
  };

  const handleEdit = (id?: string) => {
    if (!portalId) return alert("No portal selected");
    navigate(
      `/portal-configuration/${portalId}/email-templates/email-template/${id}`,
    );
  };

  const handleRemove = async (ids: string[]) => {
    if (!portalId) return alert("No portal selected");
    if (!auth.user?.UserPortalUid) return alert("User not authenticated");

    setIsDeleting(true);
    try {
      await deleteEmailTemplate(portalId, {
        emailTemplateIds: ids,
        adminUser: auth.user.UserPortalUid,
      });
      setTemplates((prev) =>
        prev.filter((t) => !ids.includes(t.emailTemplateId)),
      );
      setSelectedTemplates((prev) =>
        prev.filter((t) => !ids.includes(t.emailTemplateId)),
      );
      setConfirmDeleteModal(false);
      showToast("Email templates were successfully removed", "success");
    } catch (err) {
      alert(`Failed to ${REMOVETEXT.toLowerCase()} template(s)`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkRemove = () => {
    if (selectedTemplates.length === 0) return;
    const ids = selectedTemplates.map((t) => t.emailTemplateId);
    handleRemove(ids);
  };

  const handleAddNewTemplate = () => {
    if (!portalId) return alert("No portal selected");
    navigate(
      `/portal-configuration/${portalId}/email-templates/add-email-template`,
    );
  };

  const columns: ColumnDef<EmailTemplateResult>[] = [
    {
      id: "name",
      label: "Code",
      sortable: true,
      render: (row) => (
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(row.emailTemplateId);
          }}
          sx={{
            fontWeight: 600,
            color: brand[600],
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: "subject",
      label: "Subject",
      sortable: true,
    },
    {
      id: "sender",
      label: "From",
      sortable: true,
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
    },
  ];

  const actions = (
    <>
      {anySelected && (
        <>
          <CustomActionIconLink
            title="Remove"
            icon={<DeleteOutlineIcon />}
            onClick={() => setConfirmDeleteModal(true)}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}
      <Button
        onClick={handleAddNewTemplate}
        sx={{
          backgroundColor: `${brand[600]} !important`,
          color: `white`,
          borderRadius: "12px",
          padding: "16px",
          "&:hover": {
            bgcolor: `${brand[700]} !important`,
          },
        }}
      >
        Add new template
      </Button>
    </>
  );

  return (
    <>
      <DataTable
        data={templates}
        columns={columns}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setPage(1);
        }}
        sort={{ orderBy: sortField, order: sortDirection }}
        onSortChange={handleSortChange}
        search={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setPage(1);
        }}
        searchPlaceholder="Search by code"
        actions={actions}
        selectedCountTitle="Selected templates"
        getRowId={(row) => row.emailTemplateId}
        enableSelection={true}
        selectedRows={selectedTemplates}
        onSelectionChange={setSelectedTemplates}
        isLoading={isLoading}
      />
      <ConfirmModal
        openConfirmModal={confirmDeleteModal}
        setOpenConfirmModal={setConfirmDeleteModal}
        modalTitle={`${REMOVETEXT} selected templates`}
        modalSubtitle={`Are you sure you want to ${REMOVETEXT.toLowerCase()} the selected templates?`}
        modalLoading={isDeleting}
        handleModalConfirm={handleBulkRemove}
        confirmButtonClick={() => {}}
      />
    </>
  );
};

export default EmailTemplates;
